import api, { ResponseCallback, ResponseAndErrorCallback } from '../services/api.service'
import { AddressData } from './address.api'
import { TimezoneData } from './timezone.api'

export type Status =
    'email unverified'
    | 'email verified'
    | 'approved'
    | 'rejected'
    | 'expired'
    | 'onboarded'
    | 'closed'
export type SubscriptionType = 'regular' | 'customLabel' | 'productionCustomLabel'

export interface SubscriptionData {
    id: number
    timezoneId: number
    addressId: number
    type: SubscriptionType
    name: string
    role: string
    username: string
    email: string
    phone: string
    organization: string
    status: Status
    expireAt: string
    createdAt: string
    updatedAt: string
    timezone?: TimezoneData
    address?: AddressData
}

export interface SubscriptionPackage {
    id: number;
    name: string;
    price: number;
    description: string;
    slug: string;
    time: string;
    is_current_plan: boolean;
    is_downgrade: boolean;
    ends_on: string;
    is_subscribed: boolean;
    scheduled_downgrade?: {
        name: string;
        plan_id: number;
        starts_on: string;
    }
    renewal_date?: string;
    email?: string;
    subcsriber_name?: string;
}

export interface SubscriptionCreateRequest {
    type?: SubscriptionType
    fullName?: string
    role?: string
    username?: string
    email?: string
    phone?: string
    organizationName?: string
    timezone?: string
    address?: string
    city?: string
    state?: string
    zip?: string
}

export interface SubscriptionVerifyEmailRequest {
    token?: string
}

export interface SubscriptionVerifyEmailResponse {
    verified: boolean
}

export interface SubscriptionApproveResponse {
    approved: boolean
}

export interface SubscriptionRejectResponse {
    rejected: boolean
}

export interface SubscriptionOnboardResponse {
    onboarded: boolean
}

export interface SubscriptionCloseResponse {
    closed: boolean
}

export interface PendingPaymentRequest {
    amount: number
    packageId: number
    email: string
    name: string
    subId: number
}

export interface UserPendingPaymentRequest {
    amount: number
    packageId: number
    id: number
}

export interface PendingPayment {
    payment_intent_id: string
    client_secret: string
    customer_id: string
    customer_secret: string
}

export interface MembershipData {
    type: string
    price: string
    name: string
    status: string
}

export interface SubscriptionListResponse {
    plans: SubscriptionPackage[];
    current_plan: SubscriptionPackage;
    has_subscription: boolean;
}

export interface PaymentMethodData {
    id: string;
    object?: string;
    created?: number;
}

export interface PreviewPlanSubscription {
    is_change_plan: number;
    plan_name: string;
    credit: number;
    subtotal_excluding_tax: number;
    tax: number;
    total_due_now: number;
    next_cycle_total: string;
    remaining_days: number;
    renewal_date: string;
    effective_on: string;
    is_downgrade: boolean;
}

export interface ApiClientCredentialsResponse {
    api_key: string;
    api_secret: string;
}

export interface ApiClientData {
    api_key: string;
    is_active: boolean;
    rate_limit_per_minute: number;
    has_print_node_api_key: boolean;
    print_node_api_key: string | null;
}

export interface PrintNodeApiKeyResponse {
    has_print_node_api_key: boolean;
    print_node_api_key: string | null;
    message: string;
}

export interface ApiPrintUsageLabel {
    customLabelId: number;
    labelName: string;
    printsCount: number;
    lastPrintedAt: string | null;
}

export interface ApiPrintUsageSummary {
    startAt: string;
    endAt: string;
    totalPrints: number;
    labelsCount: number;
    labels: ApiPrintUsageLabel[];
}


const routes = {
  list: '/subscriptions',
  paymentIntent: '/payment',
  create: '/subscriptions',
  userPaymentIntent: '/payment/user/',
  membership: '/membership/',
  cancel: '/sites/cancel-subscription',
  downgrade: '/sites/plan/confirm-downgrade',
  subscribe: '/sites/subscribe',
  upgrade: '/sites/plan/confirm-upgrade',
  reactivate: '/sites/reactivate-subscription',
  previewPlanSubscription: '/sites/plan/preview-change',
  apiPrintUsageSummary: '/sites/api-print-usage-summary',
  apiPrintUsageSummaryPdf: '/sites/api-print-usage-summary/pdf',
  apiClientSecretGenerate: '/api-client/generate',
  apiClientGet: '/api-client',
  apiClientRegenerateSecret: '/api-client/regenerate-secret',
  apiClientPrintNodeApiKey: '/api-client/print-node-api-key',
}

export default {

  list (userId: number, callback: ResponseCallback<SubscriptionListResponse>) {
    api.get(routes.list, { userId }, callback)
  },

  paymentIntent (payload: PendingPaymentRequest, callback: ResponseCallback<PendingPayment>) {
    api.post(routes.paymentIntent, payload, callback)
  },

  userPaymentIntent (payload: UserPendingPaymentRequest, callback: ResponseCallback<PendingPayment>) {
    api.post(routes.userPaymentIntent + payload.id, payload, callback)
  },

  getActiveMembership (id: number, callback: ResponseCallback<MembershipData>) {
    api.get(routes.membership + id, {}, callback)
  },

  create (
    payload: SubscriptionCreateRequest,
    callback: ResponseCallback<SubscriptionData>,
    errorCallback: ResponseAndErrorCallback
  ) {
    api.postAndError(routes.create, payload, callback, errorCallback)
  },

  cancelSubscription (userId: number, callback: ResponseCallback<string>) {
    api.post(routes.cancel, { userId }, callback)
  },

  downgradeSubscription (payload: unknown, callback: ResponseCallback<string>) {
    api.post(routes.downgrade, payload, callback)
  },

  upgradeSubscription (payload: unknown, callback: ResponseCallback<string>) {
    api.post(routes.upgrade, payload, callback)
  },

  reactivateSubscription (userId: number, callback: ResponseCallback<string>) {
    api.post(routes.reactivate, { userId }, callback)
  },

  getPreviewPlanSubscription (payload: unknown, callback: ResponseCallback<PreviewPlanSubscription>) {
    api.post(routes.previewPlanSubscription, payload, callback)
  },

  getApiPrintUsageSummary (
    params: { userId: number, startAt?: string, endAt?: string },
    callback: ResponseCallback<ApiPrintUsageSummary>
  ) {
    api.get(routes.apiPrintUsageSummary, params, callback)
  },

  getApiPrintUsageSummaryPdfUrl (
    params: { userId: number, startAt?: string, endAt?: string, sortBy?: string, sortDirection?: string }
  ) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value))
      }
    })

    return `${process.env.REACT_APP_API_URL}${routes.apiPrintUsageSummaryPdf}?${searchParams.toString()}`
  },

  subscribe<TPayload = any> (
    payload: TPayload,
    callback: ResponseCallback<PaymentMethodData>
  ) {
    api.post<TPayload, PaymentMethodData>(routes.subscribe, payload, callback)
  },

  getApiClient (callback: ResponseCallback<ApiClientData | null>) {
      api.get(routes.apiClientGet, {}, callback)
  },

  generateApiClientSecret (callback: ResponseCallback<ApiClientCredentialsResponse>) {
      api.post(routes.apiClientSecretGenerate, {}, callback)
  },

  regenerateApiClientSecret (callback: ResponseCallback<ApiClientCredentialsResponse>) {
      api.post(routes.apiClientRegenerateSecret, {}, callback)
  },

  updatePrintNodeApiKey (printNodeApiKey: string, callback: ResponseCallback<PrintNodeApiKeyResponse>) {
      api.post(routes.apiClientPrintNodeApiKey, { print_node_api_key: printNodeApiKey }, callback)
  },

  deletePrintNodeApiKey (callback: ResponseCallback<PrintNodeApiKeyResponse>) {
      api.delete(routes.apiClientPrintNodeApiKey, {}, callback)
  },
}
